import type { TimeLog } from '../types';

/**
 * Reads time_logs across the v1/v2 schema window. Every view that consumes
 * TimeLog data should go through these helpers; once cleanup completes
 * (identity_v2_cleanup_done) the fallback branches collapse to direct reads.
 */

export type TargetType = 'project' | 'contract' | 'office' | 'general';

/**
 * `||` y no `??`: la cadena vacía no es NULL, así que con `??` un
 * `projectId: ''` ganaba sobre un contractId real y el turno apuntaba a la nada.
 *
 * En la base ya no quedan filas así: había 6 (las dejó la opción "General Shop
 * Time" del editor de turnos) y la migración 20260811080950 las puso a NULL y
 * añadió el CHECK `target_ids_not_blank`, que impide volver a escribirlas.
 * La defensa se queda porque un TimeLog recién construido en el cliente no pasa
 * por ese CHECK hasta que sincroniza: `''` todavía puede llegar aquí en memoria.
 */
export function getTargetId(log: TimeLog): string | null {
    return log.projectId || log.contractId || log.officeId || null;
}

/**
 * Se confía en la columna generada `target_type`, pero solo mientras el id que
 * la respalda siga ahí: la expresión vieja marcaba `'project'` las filas con
 * `project_id = ''` — 42,6 h de taller contabilizadas como horas de obra. Sin
 * id que lo sostenga, el tipo se recalcula desde los ids.
 */
export function getTargetType(log: TimeLog): TargetType {
    if (log.targetType === 'project' && log.projectId) return 'project';
    if (log.targetType === 'contract' && log.contractId) return 'contract';
    if (log.targetType === 'office' && log.officeId) return 'office';
    if (log.projectId) return 'project';
    if (log.contractId) return 'contract';
    if (log.officeId) return 'office';
    return 'general';
}

export function getTargetLabel(log: TimeLog): string {
    return log.targetLabel ?? log.projectName ?? 'General time';
}

export function getDisplayUserName(log: TimeLog): string {
    return log.userNameSnapshot ?? log.userName ?? 'Unknown';
}

/**
 * Coincidencia canónica de identidad del trabajador.
 *
 * Aquí decía que `time_logs.user_id` guarda el mismo id que `allowed_users.id`
 * y que por eso daba igual comparar contra uno u otro. Es falso: el fichaje
 * escribe `user_id` (el uid de auth) y `allowed_user_id` por separado
 * —PersonalClockPanel los manda como dos valores distintos— y en producción
 * solo coinciden en 9 de 98 filas. Por eso manda `allowed_user_id`, que es la
 * identidad de verdad, y `user_id` queda como respaldo para las filas viejas
 * que se grabaron sin él.
 */
export function isLogForUser(log: TimeLog, allowedUserId: string): boolean {
    if (!allowedUserId) return false;
    if (log.allowedUserId && log.allowedUserId === allowedUserId) return true;
    if (log.userId && log.userId === allowedUserId) return true;
    return false;
}
